const WOWHEAD_ICON_BASE_URL = 'https://wow.zamimg.com/images/wow/icons/large'
const WOWHEAD_TOOLTIP_BASE_URL = 'https://nether.wowhead.com/tooltip/item'
const BLIZZARD_ICON_BASE_URL = 'https://render.worldofwarcraft.com/us/icons/56'

const resolvedIconCache = new Map()

export function itemId(item) {
  return item?.itemId || item?.item_id || item?.id || ''
}

export function itemIconReference(item) {
  return String(item?.icon || item?.icon_url || item?.iconUrl || '').trim()
}

export function isPlaceholderItemIcon(icon) {
  return /(?:question[_-]?mark|questionmark)/i.test(String(icon || ''))
}

function iconName(icon) {
  const reference = String(icon || '').trim()
  if (!reference) return ''

  if (/^https?:\/\//i.test(reference)) {
    try {
      const filename = new URL(reference).pathname.split('/').pop() || ''
      return decodeURIComponent(filename).replace(/\.(?:jpe?g|png|webp)$/i, '')
    } catch {
      return ''
    }
  }

  return reference.replace(/\.(?:jpe?g|png|webp)$/i, '')
}

function iconUrl(baseUrl, icon) {
  const name = iconName(icon)
  if (!name || isPlaceholderItemIcon(name)) return ''
  return `${baseUrl}/${encodeURIComponent(name)}.jpg`
}

// New season assets can appear on Wowhead before the region-specific Blizzard render CDN is
// populated, so use the icon CDN that already serves the app's item tooltips.
export function itemIconUrl(item) {
  const reference = itemIconReference(item)
  if (!reference || isPlaceholderItemIcon(reference)) return ''
  if (/^https?:\/\//i.test(reference)) return reference
  return iconUrl(WOWHEAD_ICON_BASE_URL, reference)
}

export function itemIconFallbackUrls(item, resolvedIcon = '') {
  const reference = resolvedIcon || itemIconReference(item)
  if (!reference || isPlaceholderItemIcon(reference)) return []

  const isRemoteUrl = /^https?:\/\//i.test(reference)
  const isBlizzardRenderUrl = isRemoteUrl && /\/\/render(?:-[a-z]+)?\.worldofwarcraft\.com\//i.test(reference)
  const urls = isBlizzardRenderUrl
    ? [iconUrl(WOWHEAD_ICON_BASE_URL, reference), reference, iconUrl(BLIZZARD_ICON_BASE_URL, reference)]
    : [isRemoteUrl ? reference : '', iconUrl(WOWHEAD_ICON_BASE_URL, reference), iconUrl(BLIZZARD_ICON_BASE_URL, reference)]

  return [...new Set(urls.filter(Boolean))]
}

function tooltipIcon(data) {
  const candidates = [
    data?.icon,
    data?.iconName,
    data?.icon_name,
    data?.item?.icon,
    data?.data?.icon,
  ]

  return candidates.find((candidate) => candidate && !isPlaceholderItemIcon(candidate)) || ''
}

export function clearResolvedItemIconCache() {
  resolvedIconCache.clear()
}

// A placeholder is valid image metadata, not a failed request, so an <img> error handler cannot
// repair it. Resolve only missing/placeholder entries by item id and share in-flight requests.
export function resolveItemIcon(item, fetcher = globalThis.fetch) {
  const reference = itemIconReference(item)
  if (reference && !isPlaceholderItemIcon(reference)) return Promise.resolve(reference)

  const id = itemId(item)
  if (!id || typeof fetcher !== 'function') return Promise.resolve('')

  const key = String(id)
  const cached = resolvedIconCache.get(key)
  if (cached) return cached

  const request = fetcher(`${WOWHEAD_TOOLTIP_BASE_URL}/${encodeURIComponent(key)}?dataEnv=1&locale=0`, {
    headers: { Accept: 'application/json' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Wowhead item request failed: ${response.status}`)
      return response.json()
    })
    .then(tooltipIcon)
    .catch(() => '')

  resolvedIconCache.set(key, request)
  return request
}
